/**
 * Pure geometry/BOM helpers for the rig configurator. Mirrors backend services/rig.py so the UI can give instant
 * feedback (KPI, snapping, collisions) while the server remains the source of truth on save.
 *
 * Coordinates: x → east, z → south, y → up. A placed module stores its footprint min-corner (x, z) in metres,
 * its floor index and a rotation of 0/90/180/270 degrees. rot 0 → width along x, length along z.
 */

export const ROTS = [0, 90, 180, 270];
/** Module type currently dragged from the library (dataTransfer data is not readable during dragover). */
export const dragState = { typeId: null };
export const DEFAULT_FLOOR_HEIGHT = 3.0;
export const DEFAULT_GRID = 0.4;
export const SNAP_TOLERANCE = 0.75; // metres – magnetic edge snapping distance
const PER_LOAD = { light: 3, standard: 2, heavy: 1 };

export const KIND_LABELS = { bolig: "Bolig", kontor: "Kontor", kantine: "Kantine", sanitaer: "Sanitær", garderobe: "Garderobe", teknisk: "Teknisk", lager: "Lager", gang: "Gang / inngang", trapp: "Trapp", felles: "Fellesrom", vaskeri: "Vaskeri", annet: "Annet" };

/* Delivery status → quiet, distinguishable colours. Only used in delivery mode (data visualisation, not decoration). */
export const DELIVERY_COLORS = {
  ikke_tildelt: "#d5d9df",
  pa_lager: "#b8c0ca",
  reservert: "#9db7dc",
  klargjoring: "#e3c46a",
  klar_for_transport: "#e0a95a",
  pa_vei: "#d98a45",
  levert: "#9ccf9e",
  montert: "#4fae7a",
  idriftsatt: "#2f9a6a",
  i_drift: "#2f9a6a",
  service: "#d97a7a",
  demontering: "#a897d9",
  retur: "#bcb0e6",
  ikke_tilgjengelig: "#a1a1aa",
};
export const DELIVERY_LABELS = { ikke_tildelt: "Ikke tildelt", pa_lager: "På lager", reservert: "Reservert", klargjoring: "Klargjøring", klar_for_transport: "Klar for transport", pa_vei: "På vei", levert: "Levert", montert: "Montert", idriftsatt: "Idriftsatt", i_drift: "I drift", service: "Service", demontering: "Demontering", retur: "Retur", ikke_tilgjengelig: "Ikke tilgjengelig" };
export const DELIVERY_ORDER = ["ikke_tildelt", "pa_lager", "reservert", "klargjoring", "klar_for_transport", "pa_vei", "levert", "montert", "idriftsatt", "i_drift", "service", "demontering", "retur", "ikke_tilgjengelig"];

export function dims(t) {
  return { w: Number(t?.width_m) || 3.0, l: Number(t?.length_m) || 8.4, h: Number(t?.height_m) || DEFAULT_FLOOR_HEIGHT };
}

export function footprint(t, rot) {
  const d = dims(t);
  return (Number(rot) || 0) % 180 === 90 ? { sx: d.l, sz: d.w } : { sx: d.w, sz: d.l };
}

export function floorSpan(t) {
  return Math.max(1, Number(t?.floors_span) || 1);
}

export function rect(m, t) {
  const fp = footprint(t, m.rot);
  return { x0: m.x, x1: m.x + fp.sx, z0: m.z, z1: m.z + fp.sz, f0: m.floor, f1: m.floor + floorSpan(t) };
}

export function overlaps(a, ta, b, tb, eps = 0.02) {
  if (!ta || !tb) return false;
  const ra = rect(a, ta);
  const rb = rect(b, tb);
  if (ra.f1 <= rb.f0 || rb.f1 <= ra.f0) return false;
  return ra.x0 < rb.x1 - eps && rb.x0 < ra.x1 - eps && ra.z0 < rb.z1 - eps && rb.z0 < ra.z1 - eps;
}

/** Set of module ids that overlap another module (same floor range). O(n²) – fine for rigs of a few hundred modules. */
export function conflictIds(modules, types) {
  const out = new Set();
  for (let i = 0; i < modules.length; i++) {
    const a = modules[i];
    const ta = types[a.module_type_id];
    for (let j = i + 1; j < modules.length; j++) {
      const b = modules[j];
      if (overlaps(a, ta, b, types[b.module_type_id])) {
        out.add(a.id);
        out.add(b.id);
      }
    }
  }
  return out;
}

export function bounds(modules, types) {
  let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity, maxFloor = 0;
  for (const m of modules) {
    const t = types[m.module_type_id];
    if (!t) continue;
    const r = rect(m, t);
    x0 = Math.min(x0, r.x0); x1 = Math.max(x1, r.x1); z0 = Math.min(z0, r.z0); z1 = Math.max(z1, r.z1);
    maxFloor = Math.max(maxFloor, r.f1);
  }
  if (!Number.isFinite(x0)) return { x0: -10, x1: 10, z0: -10, z1: 10, cx: 0, cz: 0, size: 24, floors: 1 };
  return { x0, x1, z0, z1, cx: (x0 + x1) / 2, cz: (z0 + z1) / 2, size: Math.max(x1 - x0, z1 - z0, 12), floors: Math.max(1, maxFloor) };
}

export const snapGrid = (v, grid) => Math.round(v / grid) * grid;
export const r3 = (v) => Math.round(v * 1000) / 1000;

/**
 * Snap a candidate min-corner (x, z) for module `m` (with footprint fp) to the grid and magnetically to the edges of
 * `others` on the same floor. Returns {x, z, snappedX, snappedZ, guides[], neighbors[]} – guides are plan-view lines
 * along the aligned edges (for on-canvas feedback).
 */
export function snapPosition(x, z, fp, m, others, types, grid, tol = SNAP_TOLERANCE) {
  let sx = snapGrid(x, grid);
  let sz = snapGrid(z, grid);
  let bestX = null, bestZ = null; // {d, o, edge}
  const span = floorSpan(types[m.module_type_id]);
  for (const o of others) {
    if (o.id === m.id) continue;
    const to = types[o.module_type_id];
    if (!to) continue;
    const ro = rect(o, to);
    if (ro.f1 <= m.floor || m.floor + span <= ro.f0) continue;
    const nearZ = z < ro.z1 + tol * 2 && z + fp.sz > ro.z0 - tol * 2;
    const nearX = x < ro.x1 + tol * 2 && x + fp.sx > ro.x0 - tol * 2;
    if (nearZ) {
      for (const [cand, edge] of [[ro.x1, ro.x1], [ro.x0 - fp.sx, ro.x0], [ro.x0, ro.x0], [ro.x1 - fp.sx, ro.x1]]) {
        const d = cand - x;
        if (Math.abs(d) <= tol && (bestX === null || Math.abs(d) < Math.abs(bestX.d))) bestX = { d, o, edge, ro };
      }
    }
    if (nearX) {
      for (const [cand, edge] of [[ro.z1, ro.z1], [ro.z0 - fp.sz, ro.z0], [ro.z0, ro.z0], [ro.z1 - fp.sz, ro.z1]]) {
        const d = cand - z;
        if (Math.abs(d) <= tol && (bestZ === null || Math.abs(d) < Math.abs(bestZ.d))) bestZ = { d, o, edge, ro };
      }
    }
  }
  if (bestX) sx = x + bestX.d;
  if (bestZ) sz = z + bestZ.d;
  const guides = [];
  const neighbors = [];
  if (bestX) {
    guides.push({ axis: "x", at: r3(bestX.edge), from: Math.min(sz, bestX.ro.z0) - 1.5, to: Math.max(sz + fp.sz, bestX.ro.z1) + 1.5 });
    neighbors.push(bestX.o);
  }
  if (bestZ) {
    guides.push({ axis: "z", at: r3(bestZ.edge), from: Math.min(sx, bestZ.ro.x0) - 1.5, to: Math.max(sx + fp.sx, bestZ.ro.x1) + 1.5 });
    if (!neighbors.includes(bestZ.o)) neighbors.push(bestZ.o);
  }
  return { x: r3(sx), z: r3(sz), snappedX: !!bestX, snappedZ: !!bestZ, guides, neighbors };
}

/** Rotate a module 90° around its footprint centre so it stays in place. */
export function rotated(m, t, delta = 90) {
  const fp = footprint(t, m.rot);
  const cx = m.x + fp.sx / 2;
  const cz = m.z + fp.sz / 2;
  const rot = (((m.rot || 0) + delta) % 360 + 360) % 360;
  const nf = footprint(t, rot);
  return { ...m, rot, x: r3(cx - nf.sx / 2), z: r3(cz - nf.sz / 2) };
}

/** Find a free spot for a new module of type `t` on `floor`: right of the east-most module on that floor, else origin. */
export function suggestPlacement(modules, types, t, floor, rot = 0) {
  const same = modules.filter((m) => m.floor === floor && types[m.module_type_id]);
  if (!same.length) return { x: 0, z: 0 };
  let best = null;
  for (const m of same) {
    const r = rect(m, types[m.module_type_id]);
    if (!best || r.x1 > best.x1 || (r.x1 === best.x1 && r.z0 < best.z0)) best = r;
  }
  const cand = { x: r3(best.x1), z: r3(best.z0) };
  const probe = { id: "__probe", module_type_id: t.id, floor, rot, ...cand };
  if (!modules.some((m) => overlaps(probe, t, m, types[m.module_type_id]))) return cand;
  // fall back: south of the block
  const b = bounds(same, types);
  return { x: r3(b.x0), z: r3(b.z1 + 2) };
}

export function nextLabel(modules, t) {
  const prefix = t?.code || "M";
  let n = 1;
  const used = new Set(modules.map((m) => m.label));
  while (used.has(`${prefix}-${String(n).padStart(2, "0")}`)) n++;
  return `${prefix}-${String(n).padStart(2, "0")}`;
}

/** Instant KPI/BOM from the working copy (same rules as the backend summary; estimate comes from the server). */
export function localSummary(modules, types, capacityTarget = 0) {
  const bom = new Map();
  let beds = 0, area = 0, weight = 0, loads = 0, hours = 0;
  const perFloor = {};
  for (const m of modules) {
    const t = types[m.module_type_id];
    if (!t) continue;
    const row = bom.get(t.id) || { module_type_id: t.id, code: t.code, name: t.name, kind: t.kind, color: t.color, quantity: 0, beds: 0, area_m2: 0, weight_kg: 0, monthly_rent: Number(t.monthly_rent) || 0, linked_assets: 0 };
    row.quantity += 1;
    row.beds += Number(t.beds) || 0;
    row.area_m2 += Number(t.area_m2) || 0;
    row.weight_kg += Number(t.weight_kg) || 0;
    row.linked_assets += m.asset_id ? 1 : 0;
    bom.set(t.id, row);
    beds += Number(t.beds) || 0;
    area += Number(t.area_m2) || 0;
    weight += Number(t.weight_kg) || 0;
    loads += 1 / (PER_LOAD[t.transport_class] || 2);
    hours += Number(t.assembly_hours_per_module) || 4;
    perFloor[m.floor] = (perFloor[m.floor] || 0) + 1;
  }
  const transports = modules.length ? Math.ceil(loads - 1e-9) : 0;
  const rows = [...bom.values()].map((r) => ({ ...r, area_m2: Math.round(r.area_m2 * 10) / 10, monthly_total: Math.round(r.quantity * r.monthly_rent) }));
  const order = ["bolig", "gang", "trapp", "teknisk", "felles", "vaskeri", "sanitaer", "kantine", "kontor", "garderobe", "lager", "annet"];
  rows.sort((a, b) => (order.indexOf(a.kind) - order.indexOf(b.kind)) || b.quantity - a.quantity || String(a.code).localeCompare(String(b.code)));
  return {
    kpi: { modules: modules.length, beds, area_m2: Math.round(area), weight_kg: Math.round(weight), weight_t: Math.round(weight / 100) / 10, transports, delivery_days: transports ? Math.ceil(transports / 4) : 0, assembly_hours: Math.round(hours), per_floor: perFloor, capacity_target: capacityTarget, monthly_rent: rows.reduce((s, r) => s + r.monthly_total, 0) },
    bom: rows,
  };
}

/** Stable signature of the BOM – the estimate only changes when this changes. */
export function bomSignature(modules) {
  const counts = {};
  for (const m of modules) counts[m.module_type_id] = (counts[m.module_type_id] || 0) + 1;
  return Object.keys(counts).sort().map((k) => `${k}:${counts[k]}`).join("|");
}

export function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function shade(hex, amount) {
  const [r, g, b] = hexToRgb(hex);
  const f = (c) => Math.max(0, Math.min(255, Math.round(c + (amount < 0 ? c * amount : (255 - c) * amount))));
  return `#${[f(r), f(g), f(b)].map((c) => c.toString(16).padStart(2, "0")).join("")}`;
}

/* ---------------- adjacency & design health ---------------- */

/** True when two rectangles on the same floor share an edge (touching within `eps`) over at least `minShared` metres. */
export function touching(ra, rb, eps = 0.06, minShared = 0.5) {
  const sharedZ = Math.min(ra.z1, rb.z1) - Math.max(ra.z0, rb.z0);
  const sharedX = Math.min(ra.x1, rb.x1) - Math.max(ra.x0, rb.x0);
  if (sharedZ >= minShared && (Math.abs(ra.x1 - rb.x0) <= eps || Math.abs(rb.x1 - ra.x0) <= eps)) return true;
  if (sharedX >= minShared && (Math.abs(ra.z1 - rb.z0) <= eps || Math.abs(rb.z1 - ra.z0) <= eps)) return true;
  return false;
}

/**
 * Automatic design validation. Returns {checks: [{id, level: ok|warn|error|info, title, detail, moduleIds}], warnings, errors}.
 */
export function designHealth(modules, types, { floors = 1, capacityTarget = 0, conflicts = new Set() } = {}) {
  const checks = [];
  const withType = modules.filter((m) => types[m.module_type_id]);
  const rects = new Map(withType.map((m) => [m.id, rect(m, types[m.module_type_id])]));
  const kindOf = (m) => types[m.module_type_id]?.kind;
  const beds = withType.reduce((s, m) => s + (Number(types[m.module_type_id].beds) || 0), 0);

  // capacity
  if (capacityTarget > 0) {
    checks.push(beds >= capacityTarget
      ? { id: "capacity", level: "ok", title: "Kapasitetskrav oppfylt", detail: `${beds} senger for ${capacityTarget} personer` }
      : { id: "capacity", level: "warn", title: "Kapasitetskrav ikke oppfylt", detail: `${beds} av ${capacityTarget} senger – ${capacityTarget - beds} mangler` });
  }
  // collisions
  checks.push(conflicts.size
    ? { id: "collisions", level: "error", title: `${conflicts.size} moduler overlapper`, detail: "Geometriske kollisjoner må løses før produksjon", moduleIds: [...conflicts] }
    : { id: "collisions", level: "ok", title: "Ingen geometriske kollisjoner" });
  // corridor access: every bolig must be connected (via touching modules) to a gang/trapp module on its floor
  const gangs = withType.filter((m) => ["gang", "trapp"].includes(kindOf(m)));
  const bolig = withType.filter((m) => kindOf(m) === "bolig");
  if (bolig.length && gangs.length) {
    const noAccess = [];
    const byFloor = {};
    for (const m of withType) (byFloor[m.floor] = byFloor[m.floor] || []).push(m);
    for (const f of Object.keys(byFloor)) {
      const list = byFloor[f];
      const adj = new Map(list.map((m) => [m.id, []]));
      for (let i = 0; i < list.length; i++) for (let j = i + 1; j < list.length; j++) {
        if (touching(rects.get(list[i].id), rects.get(list[j].id))) { adj.get(list[i].id).push(list[j].id); adj.get(list[j].id).push(list[i].id); }
      }
      // stair towers span floors – treat them as present on every floor they cover
      const seeds = new Set(list.filter((m) => ["gang", "trapp"].includes(kindOf(m))).map((m) => m.id));
      for (const g of gangs) { const r = rects.get(g.id); if (r.f0 <= Number(f) && Number(f) < r.f1 && !seeds.has(g.id)) { for (const m of list) if (touching(r, rects.get(m.id))) adj.get(m.id).push(g.id); seeds.add(g.id); adj.set(g.id, adj.get(g.id) || []); } }
      const seen = new Set(seeds);
      const queue = [...seeds];
      while (queue.length) { const id = queue.pop(); for (const n of adj.get(id) || []) if (!seen.has(n)) { seen.add(n); queue.push(n); } }
      for (const m of list) if (kindOf(m) === "bolig" && !seen.has(m.id)) noAccess.push(m.id);
    }
    checks.push(noAccess.length
      ? { id: "access", level: "warn", title: `${noAccess.length} boligmoduler uten gangtilgang`, detail: "Modulene er ikke koblet til gang eller trapp via nabomoduler", moduleIds: noAccess }
      : { id: "access", level: "ok", title: "Alle boligmoduler har gangtilgang" });
  } else if (bolig.length) {
    checks.push({ id: "access", level: "info", title: "Ingen gang-/trappemoduler i designet", detail: "Legg til gangmoduler for adkomst" });
  }
  // stairs
  if (floors > 1 || withType.some((m) => m.floor > 0)) {
    const trapp = withType.filter((m) => kindOf(m) === "trapp");
    checks.push(trapp.length ? { id: "stairs", level: "ok", title: `${trapp.length} trappetårn for ${floors} etasjer` } : { id: "stairs", level: "warn", title: "Mangler trappetårn", detail: "Riggen har flere etasjer men ingen trappemodul" });
  }
  // support: upper-floor modules must rest on something below
  const floating = [];
  for (const m of withType) {
    if (m.floor === 0) continue;
    const r = rects.get(m.id);
    const area = (r.x1 - r.x0) * (r.z1 - r.z0);
    let covered = 0;
    for (const o of withType) {
      if (o.id === m.id) continue;
      const ro = rects.get(o.id);
      if (!(ro.f0 <= m.floor - 1 && m.floor - 1 < ro.f1)) continue;
      const ox = Math.max(0, Math.min(r.x1, ro.x1) - Math.max(r.x0, ro.x0));
      const oz = Math.max(0, Math.min(r.z1, ro.z1) - Math.max(r.z0, ro.z0));
      covered += ox * oz;
    }
    if (covered < area * 0.45) floating.push(m.id);
  }
  if (withType.some((m) => m.floor > 0)) {
    checks.push(floating.length ? { id: "support", level: "error", title: `${floating.length} moduler mangler understøttelse`, detail: "Moduler i etasje 2+ må stå på moduler under", moduleIds: floating } : { id: "support", level: "ok", title: "Alle etasjemoduler er understøttet" });
  }
  // technical supply
  const tek = withType.filter((m) => kindOf(m) === "teknisk");
  if (withType.length > 20 && !tek.length) checks.push({ id: "tech", level: "warn", title: "Mangler teknisk modul", detail: "Rigger over 20 moduler krever hovedfordeling (teknisk modul)" });
  else if (tek.length && bolig.length) {
    const far = [];
    const centers = tek.map((t) => { const r = rects.get(t.id); return [(r.x0 + r.x1) / 2, (r.z0 + r.z1) / 2]; });
    for (const b of bolig) {
      const r = rects.get(b.id); const c = [(r.x0 + r.x1) / 2, (r.z0 + r.z1) / 2];
      const d = Math.min(...centers.map(([x, z]) => Math.hypot(x - c[0], z - c[1])));
      if (d > 45) far.push(b.id);
    }
    checks.push(far.length ? { id: "tech", level: "warn", title: `${far.length} boligmoduler >45 m fra teknisk modul`, detail: "Vurder en ekstra teknisk modul for kortere føringsveier", moduleIds: far } : { id: "tech", level: "ok", title: `${tek.length} teknisk${tek.length > 1 ? "e" : ""} modul${tek.length > 1 ? "er" : ""} innen rekkevidde` });
  }
  // fleet assignment
  const unassigned = withType.filter((m) => !m.asset_id);
  if (withType.length) checks.push(unassigned.length ? { id: "assets", level: "info", title: `${unassigned.length} plasseringer uten fysisk enhet`, detail: "Kobles fra allokeringer eller ved klargjøring", moduleIds: unassigned.map((m) => m.id) } : { id: "assets", level: "ok", title: "Alle plasseringer har fysisk enhet" });

  const warnings = checks.filter((c) => c.level === "warn" || c.level === "info").length;
  const errors = checks.filter((c) => c.level === "error").length;
  return { checks, warnings, errors, issues: checks.filter((c) => c.level !== "ok") };
}

/* ---------------- library filters & connection points ---------------- */

export const LIBRARY_FILTERS = [
  { id: "alle", label: "Alle", kinds: null },
  { id: "bolig", label: "Bolig", kinds: ["bolig"] },
  { id: "gang", label: "Gang", kinds: ["gang"] },
  { id: "trapp", label: "Trapp", kinds: ["trapp"] },
  { id: "teknisk", label: "Teknisk", kinds: ["teknisk"] },
  { id: "fasiliteter", label: "Fasiliteter", kinds: ["felles", "vaskeri", "sanitaer", "kantine", "garderobe", "lager"] },
  { id: "kontor", label: "Kontor", kinds: ["kontor"] },
];

export const SIDES = ["north", "east", "south", "west"];
export const SIDE_LABELS = { north: "Nord", east: "Øst", south: "Sør", west: "Vest" };
export const SIDE_SHORT = { north: "N", east: "Ø", south: "S", west: "V" };
export const TECH_CONN_LABELS = { el: "El", vann: "Vann", avlop: "Avløp", vent: "Ventilasjon", data: "Data" };
export const sideConnections = (t) => (t?.connections || []).filter((c) => SIDES.includes(c));
export const techConnections = (t) => (t?.connections || []).filter((c) => !SIDES.includes(c));

/** Local side → world side for a module rotated `rot` degrees (rotation is clockwise seen from above). */
export function worldSide(localSide, rot) {
  const i = SIDES.indexOf(localSide);
  if (i < 0) return localSide;
  const steps = ((((Number(rot) || 0) / 90) % 4) + 4) % 4;
  return SIDES[(i + steps) % 4];
}

/**
 * Connection points of a placed module: midpoints of its connectable sides in world space, plus the offset from the
 * footprint centre (for rendering inside the module group).
 */
export function connectionPoints(m, t) {
  const fp = footprint(t, m.rot);
  const cx = m.x + fp.sx / 2;
  const cz = m.z + fp.sz / 2;
  return sideConnections(t).map((local) => {
    const side = worldSide(local, m.rot);
    const p = side === "north" ? { x: cx, z: m.z } : side === "south" ? { x: cx, z: m.z + fp.sz } : side === "east" ? { x: m.x + fp.sx, z: cz } : { x: m.x, z: cz };
    return { side, local, x: p.x, z: p.z, dx: p.x - cx, dz: p.z - cz };
  });
}

/** Compact currency: 10 893 448 → "10,89 MNOK". */
export function mnok(v) {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toLocaleString("nb-NO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MNOK`;
  if (Math.abs(n) >= 1e3) return `${Math.round(n / 1e3).toLocaleString("nb-NO")} kNOK`;
  return `${Math.round(n).toLocaleString("nb-NO")} kr`;
}
